/**
 * Choosing a driver for an order.
 *
 * The list comes ordered from the API — nearest first by road for the top few, straight
 * line for the rest, each nudged by the driver's recent record (`Candidates.cs` in the
 * Dispatch module is where that is decided and explained). It is a shortlist, not a
 * decision — a dispatcher who knows the city will sometimes pick the third one because the
 * nearest is on the wrong side of a river, and the point of manual dispatch is to let them.
 *
 * Three are shown and the rest wait behind one button. The order detail opens by itself
 * for every order taken, and with the automation usually holding a driver already, a list
 * of ten cars under every order is noise; three is what a dispatcher compares at a glance.
 */

import { useCallback, useEffect, useState } from 'react';

/** How many candidates are shown before the dispatcher asks for more. */
const SHOWN_AT_FIRST = 3;
import { useTranslation } from 'react-i18next';

import { ApiError, errorKey } from '@taxi/api-client';

import { Banner, Button, EmptyState, Sub } from '../ui';
import { dispatchApi, formatDistance, type DriverCandidate } from './api';

/** Beyond this, a position is old enough that the driver may well have moved on. */
const STALE_POSITION_SECONDS = 60;

export function AssignPanel({
  orderId,
  currentDriverId,
  onAssigned,
  limit,
}: {
  orderId: string;
  currentDriverId: string | null;
  onAssigned: () => void;
  /** The map view's floating card shows the two nearest and nothing else. */
  limit?: number;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'bg';

  const [candidates, setCandidates] = useState<DriverCandidate[] | null>(null);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  // Folded again for the next order: wanting the whole list for one order says nothing
  // about the next, and a panel that stayed open would grow on every order after the first.
  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect
    setExpanded(false);
  }, [orderId]);

  const load = useCallback(async () => {
    try {
      const found = await dispatchApi.candidates(orderId);
      setCandidates(found);
    } catch {
      // An empty list and a failed fetch look the same to a dispatcher, and neither is
      // worth an error banner over the thing they are trying to do.
      setCandidates([]);
    }
  }, [orderId]);

  useEffect(() => {
    // Deliberately not clearing the list first. Blanking it on every refresh would make the
    // panel flicker each time an order changes, and a dispatcher reading a name off it
    // would lose their place.
    //
    // The lint rule below sees a setState reachable from an effect and assumes a cascading
    // render. It is a false positive here: load is async, so the state is set after an
    // await rather than during this effect.
    // eslint-disable-next-line react/set-state-in-effect
    void load();
  }, [load]);

  async function assign(driverId: string) {
    setAssigning(driverId);
    setError(null);

    try {
      await dispatchApi.assign(orderId, driverId);
      onAssigned();
    } catch (failure) {
      // The API answers with a code — driver_unavailable, driver_on_another_order — and
      // this turns it into the dispatcher's own language. Showing the raw code, or the
      // English developer message, would be showing them our internals.
      setError(
        failure instanceof ApiError
          ? t(errorKey(failure.problem), {
              ...failure.problem.arguments,
              defaultValue: t('errors.unknown'),
            })
          : t('errors.unknown'),
      );

      // Someone else took that driver between the list being drawn and the click. The list
      // is refreshed so the next choice is made against the truth.
      await load();
    } finally {
      setAssigning(null);
    }
  }

  if (candidates === null) return <EmptyState>{t('app.loading')}</EmptyState>;

  // A caller's own limit (the map's floating card) is final; otherwise three, or everything
  // once asked for.
  const shown =
    limit !== undefined
      ? candidates.slice(0, limit)
      : expanded
        ? candidates
        : candidates.slice(0, SHOWN_AT_FIRST);
  const hidden = limit === undefined ? candidates.length - shown.length : 0;

  return (
    <div>
      <div className="flex items-center justify-between gap-2 px-3.5 pt-2 pb-1">
        <span className="text-caps font-semibold tracking-[0.06em] uppercase text-ink-3">
          {currentDriverId === null ? t('assign.title') : t('assign.reassign')}
        </span>
        <Button size="sm" variant="quiet" onClick={() => void load()}>
          {t('assign.refresh')}
        </Button>
      </div>

      {error !== null && (
        <Banner tone="danger" className="mx-3.5 mb-2">
          {error}
        </Banner>
      )}

      {shown.length === 0 ? (
        <EmptyState>{t('assign.noneAvailable')}</EmptyState>
      ) : (
        <ul className="m-0 p-0 list-none">
          {shown.map((candidate, at) => {
            const stale = candidate.locationAgeSeconds > STALE_POSITION_SECONDS;
            const holding = candidate.driverId === currentDriverId;

            return (
              <li
                key={candidate.driverId}
                className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-3 items-center px-3.5 py-2 border-b border-rule last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="font-semibold truncate">
                    {candidate.displayName}
                    {candidate.callSign !== null && (
                      <span className="text-ink-3 font-normal"> · {candidate.callSign}</span>
                    )}
                  </div>
                  {candidate.vehiclePlate !== null && <Sub block>{candidate.vehiclePlate}</Sub>}
                </div>

                <div className="text-right text-secondary">
                  <div className="tabular-nums">
                    {formatDistance(candidate.distanceMetres, locale)}
                  </div>
                  {/* A stale fix is worth saying out loud: the car may not be where the
                      distance claims, and the dispatcher is about to promise a time. */}
                  {stale && (
                    <div className="text-warn tabular-nums" title={t('assign.staleHint')}>
                      {t('assign.stale', { seconds: Math.round(candidate.locationAgeSeconds) })}
                    </div>
                  )}
                </div>

                {holding ? (
                  <span className="text-ink-3 text-secondary">{t('assign.holding')}</span>
                ) : (
                  <Button
                    size="sm"
                    variant={at === 0 ? 'primary' : 'outline'}
                    disabled={assigning !== null}
                    onClick={() => void assign(candidate.driverId)}
                  >
                    {assigning === candidate.driverId ? t('assign.assigning') : t('assign.assign')}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* The rest of the list, one click away, and one click back. Quiet, because the
          primary on this panel is the first Assign and nothing may compete with it. */}
      {(hidden > 0 || (expanded && candidates.length > SHOWN_AT_FIRST)) && (
        <div className="px-3.5 py-1.5 border-t border-rule">
          <Button size="sm" variant="quiet" onClick={() => setExpanded((open) => !open)}>
            {expanded ? t('assign.fewer') : t('assign.more', { count: hidden })}
          </Button>
        </div>
      )}
    </div>
  );
}
